// feature/remanente-export — Sprint P3. REMANENTE Excel export
// panel. Two date inputs + a "Generar Excel" button. On click,
// downloads the xlsx as a binary blob. If the endpoint returns 404
// no_rows, renders the "no hay casos contestados" inline message
// instead of triggering a download with an empty file. Visible to
// admin/carpetas/directora — gated at the call site (Exports.js).
import React, { useState } from "react";
import { exportsApi } from "../../api/endpoints";
import Spinner from "../Spinner";
import { useToast } from "../Toast";

function firstOfCurrentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}
function today() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function RemanenteExport() {
  const toast = useToast();
  const [startDate, setStartDate] = useState(firstOfCurrentMonth());
  const [endDate, setEndDate] = useState(today());
  const [busy, setBusy] = useState(false);
  const [emptyMsg, setEmptyMsg] = useState(null);

  async function handleDownload() {
    setEmptyMsg(null);
    setBusy(true);
    try {
      const res = await exportsApi.remanente(startDate, endDate);
      // Pull filename from Content-Disposition if present.
      const cd = res.headers?.["content-disposition"] || "";
      const match = cd.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `REMANENTE_${startDate}_${endDate}.xlsx`;

      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.push(`Descargado: ${filename}`, "success");
    } catch (err) {
      // 404 no_rows arrives as a Blob with JSON content (because
      // axios responseType is "blob"). Parse to surface the message.
      const status = err?.response?.status;
      if (status === 404) {
        try {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          if (parsed.error === "no_rows") {
            setEmptyMsg(parsed.message || "No hay casos contestados en el rango seleccionado.");
            return;
          }
        } catch (_) { /* fall through */ }
      }
      if (status === 403) {
        toast.push("Sin permisos para descargar el REMANENTE.", "error");
        return;
      }
      if (status === 400) {
        try {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          toast.push(parsed.message || "Rango de fechas inválido.", "error");
        } catch (_) {
          toast.push("Rango de fechas inválido.", "error");
        }
        return;
      }
      toast.push("Error al generar el Excel.", "error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="glass-section">
      <div className="row" style={{ alignItems: "center", marginBottom: 8 }}>
        <h3 style={{ margin: 0 }}>Exportación REMANENTE</h3>
        <span className="muted" style={{ fontSize: 11, marginLeft: 8 }}>
          Excel mensual de casos contestados para Previsora
        </span>
      </div>
      <div className="muted" style={{ fontSize: 12, marginBottom: 12 }}>
        Genera el Excel con las 18 columnas del formato REMANENTE. Filtra por la fecha en que el caso fue marcado como contestado.
      </div>

      <div className="row" style={{ gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
        <div>
          <label>Desde</label>
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </div>
        <div>
          <label>Hasta</label>
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        </div>
        <div style={{ flex: 1 }} />
        <button onClick={handleDownload} disabled={busy || !startDate || !endDate}>
          {busy && <Spinner color="currentColor" />} Generar Excel
        </button>
      </div>

      {emptyMsg && (
        <div className="warn-inline" style={{
          marginTop: 12, padding: 10, fontSize: 13,
          background: "var(--color-info-bg)",
          borderColor: "var(--accent-mint)",
          color: "var(--text-body)",
        }}>
          {emptyMsg}
        </div>
      )}
    </div>
  );
}
